import React, { useState, useMemo, useEffect } from 'react';
import { useEmployees, useEmployeeDetails, useVerifyEmployee, useRejectEmployee, useVerifyDocument, useJobRoles } from '../../hooks/useAdmin';
import { debounce } from '../../utils/helpers';
import TableHeader from '../../components/common/TableHeader';
import UserDetailModal from '../../components/admin/UserDetailModal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import RejectDialog from '../../components/common/RejectDialog';
import './AdminEmployees.css';

const AdminEmployees = () => {
  const [filters, setFilters] = useState({
    verification_status: 'all',
    search: '',
    job_role_id: ''
  });
  
  const [searchInput, setSearchInput] = useState('');
  
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc'
  });
  
  const [selectedUserId, setSelectedUserId] = useState(null);
  
  // Fetch job roles for filter dropdown
  const { data: jobRoles } = useJobRoles();
  
  // Build backend filters (remove empty values)
  const backendFilters = useMemo(() => {
    const backendFilter = {};
    if (filters.verification_status && filters.verification_status !== 'all') {
      backendFilter.verification_status = filters.verification_status;
    }
    if (filters.search) {
      backendFilter.search = filters.search;
    }
    if (filters.job_role_id) {
      backendFilter.job_role_id = filters.job_role_id;
    }
    return backendFilter;
  }, [filters]);
  
  const { data: employees, isLoading } = useEmployees(backendFilters);
  const { data: selectedUserDetails, isLoading: isLoadingDetails } = useEmployeeDetails(selectedUserId);
  const verifyMutation = useVerifyEmployee();
  const rejectMutation = useRejectEmployee();
  const verifyDocumentMutation = useVerifyDocument();

  // Debounced search handler - use useMemo to persist the debounced function
  const debouncedSearch = useMemo(
    () => debounce((searchValue) => {
      setFilters(prev => ({ ...prev, search: searchValue }));
    }, 300),
    []
  );

  // Update search input and trigger debounced search
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    debouncedSearch(value);
  };

  // Sync searchInput with filters.search on mount or when filters.search changes externally
  useEffect(() => {
    if (filters.search !== searchInput) {
      setSearchInput(filters.search);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search]);

  const handleSort = (key, direction) => {
    setSortConfig({ key, direction });
  };

  const sortedEmployees = useMemo(() => {
    if (!employees || !sortConfig.key) return employees;
    
    return [...employees].sort((a, b) => {
      let aVal, bVal;
      
      switch (sortConfig.key) {
        case 'name':
          aVal = `${a.first_name} ${a.last_name}`.toLowerCase();
          bVal = `${b.first_name} ${b.last_name}`.toLowerCase();
          break;
        case 'email':
          aVal = a.email?.toLowerCase() || '';
          bVal = b.email?.toLowerCase() || '';
          break;
        case 'job_role':
          aVal = (a.job_role_detail?.job_role_name || a.job_role || '').toLowerCase();
          bVal = (b.job_role_detail?.job_role_name || b.job_role || '').toLowerCase();
          break;
        case 'gdc_number':
          aVal = a.gdc_number || '';
          bVal = b.gdc_number || '';
          break;
        case 'status':
          aVal = a.is_verified_by_admin ? 1 : 0;
          bVal = b.is_verified_by_admin ? 1 : 0;
          break;
        case 'joined':
          aVal = a.date_joined ? new Date(a.date_joined).getTime() : 0;
          bVal = b.date_joined ? new Date(b.date_joined).getTime() : 0;
          break;
        default:
          return 0;
      }
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [employees, sortConfig]);

  const [showConfirmVerify, setShowConfirmVerify] = useState(null);
  const [showConfirmReject, setShowConfirmReject] = useState(null);
  const [rejectComment, setRejectComment] = useState('');

  const handleVerify = (id) => {
    setShowConfirmVerify(id);
  };

  const handleConfirmVerify = () => {
    if (showConfirmVerify) {
      verifyMutation.mutate({ id: showConfirmVerify, comment: '' });
      setShowConfirmVerify(null);
    }
  };

  const handleReject = (id, comment) => {
    rejectMutation.mutate({ id, comment });
  };

  const handleRejectClick = (id) => {
    setShowConfirmReject(id);
    setRejectComment('');
  };

  const handleConfirmReject = () => {
    if (showConfirmReject && rejectComment.trim()) {
      handleReject(showConfirmReject, rejectComment);
      setShowConfirmReject(null);
      setRejectComment('');
    }
  };

  const handleViewDetails = (id) => {
    setSelectedUserId(id);
  };

  const handleCloseModal = () => {
    setSelectedUserId(null);
  };

  const handleVerifyFromModal = (id) => {
    handleVerify(id);
    handleCloseModal();
  };

  const handleRejectFromModal = (id, comment) => {
    handleReject(id, comment);
    handleCloseModal();
  };

  const handleVerifyDocument = (documentId, isVerified, userId) => {
    verifyDocumentMutation.mutate({ documentId, isVerified, userId });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-GB');
  };

  if (isLoading) {
    return (
      <div className="admin-employees__loading">
        <div className="admin-employees__spinner"></div>
        <p>Loading employees...</p>
      </div>
    );
  }

  return (
    <div className="admin-employees">
      <div className="admin-employees__header">
        <h1 className="admin-employees__title">Employee Management</h1>
        <div className="admin-employees__filters">
          <select
            value={filters.verification_status}
            onChange={(e) => setFilters({ ...filters, verification_status: e.target.value })}
            className="admin-employees__filter"
          >
            <option value="all">All Employees</option>
            <option value="pending">Pending Verification</option>
            <option value="verified">Verified</option>
          </select>
          <select
            value={filters.job_role_id}
            onChange={(e) => setFilters({ ...filters, job_role_id: e.target.value })}
            className="admin-employees__filter"
          >
            <option value="">All Job Roles</option>
            {jobRoles && jobRoles.map((role) => (
              <option key={role.id} value={role.id}>
                {role.job_role_name}
              </option>
            ))}
          </select>
          <input
            type="text"
            placeholder="Search employees..."
            value={searchInput}
            onChange={handleSearchChange}
            className="admin-employees__search"
          />
        </div>
      </div>

      {/* Desktop Table View */}
      <div className="admin-employees__table-container admin-employees__table-container--desktop">
        <table className="admin-employees__table">
          <thead>
            <tr>
              <TableHeader 
                sortKey="name" 
                currentSortKey={sortConfig.key} 
                sortDirection={sortConfig.direction}
                onSort={handleSort}
              >
                Name
              </TableHeader>
              <TableHeader 
                sortKey="email" 
                currentSortKey={sortConfig.key} 
                sortDirection={sortConfig.direction}
                onSort={handleSort}
              >
                Email
              </TableHeader>
              <TableHeader 
                sortKey="job_role" 
                currentSortKey={sortConfig.key} 
                sortDirection={sortConfig.direction}
                onSort={handleSort}
              >
                Job Role
              </TableHeader>
              <TableHeader 
                sortKey="gdc_number" 
                currentSortKey={sortConfig.key} 
                sortDirection={sortConfig.direction}
                onSort={handleSort}
              >
                GDC Number
              </TableHeader>
              <TableHeader 
                sortKey="status" 
                currentSortKey={sortConfig.key} 
                sortDirection={sortConfig.direction}
                onSort={handleSort}
              >
                Status
              </TableHeader>
              <TableHeader 
                sortKey="joined" 
                currentSortKey={sortConfig.key} 
                sortDirection={sortConfig.direction}
                onSort={handleSort}
              >
                Joined
              </TableHeader>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedEmployees && sortedEmployees.length > 0 ? (
              sortedEmployees.map((employee) => (
                <tr key={employee.id}>
                  <td>{employee.first_name} {employee.last_name}</td>
                  <td>{employee.email}</td>
                  <td>{employee.job_role_detail?.job_role_name || 'N/A'}</td>
                  <td>{employee.gdc_number || 'N/A'}</td>
                  <td>
                    <span className={`admin-employees__status admin-employees__status--${employee.is_verified_by_admin ? 'verified' : 'pending'}`}>
                      {employee.is_verified_by_admin ? 'Verified' : 'Pending'}
                    </span>
                  </td>
                  <td>{formatDate(employee.date_joined)}</td>
                  <td>
                    <div className="admin-employees__actions">
                      <button
                        onClick={() => handleViewDetails(employee.id)}
                        className="admin-employees__btn admin-employees__btn--view"
                      >
                        View Details
                      </button>
                      {!employee.is_verified_by_admin && (
                        <>
                          <button
                            onClick={() => handleVerify(employee.id)}
                            className="admin-employees__btn admin-employees__btn--verify"
                            disabled={verifyMutation.isPending}
                          >
                            Verify
                          </button>
                          <button
                            onClick={() => handleRejectClick(employee.id)}
                            className="admin-employees__btn admin-employees__btn--reject"
                            disabled={rejectMutation.isPending}
                          >
                            Reject
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="admin-employees__empty">No employees found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="admin-employees__card-container admin-employees__card-container--mobile">
        {sortedEmployees && sortedEmployees.length > 0 ? (
          sortedEmployees.map((employee) => (
            <div key={employee.id} className="admin-employees__card">
              <div className="admin-employees__card-header">
                <div>
                  <h3 className="admin-employees__card-name">
                    {employee.first_name} {employee.last_name}
                  </h3>
                  <p className="admin-employees__card-email">{employee.email}</p>
                </div>
                <span className={`admin-employees__status admin-employees__status--${employee.is_verified_by_admin ? 'verified' : 'pending'}`}>
                  {employee.is_verified_by_admin ? 'Verified' : 'Pending'}
                </span>
              </div>
              <div className="admin-employees__card-body">
                <div className="admin-employees__card-row">
                  <span className="admin-employees__card-label">Job Role:</span>
                  <span className="admin-employees__card-value">{employee.job_role_detail?.job_role_name || 'N/A'}</span>
                </div>
                <div className="admin-employees__card-row">
                  <span className="admin-employees__card-label">GDC Number:</span>
                  <span className="admin-employees__card-value">{employee.gdc_number || 'N/A'}</span>
                </div>
                <div className="admin-employees__card-row">
                  <span className="admin-employees__card-label">Joined:</span>
                  <span className="admin-employees__card-value">{formatDate(employee.date_joined)}</span>
                </div>
              </div>
              <div className="admin-employees__card-actions">
                <button
                  onClick={() => handleViewDetails(employee.id)}
                  className="admin-employees__btn admin-employees__btn--view admin-employees__btn--mobile"
                >
                  View Details
                </button>
                {!employee.is_verified_by_admin && (
                  <>
                    <button
                      onClick={() => handleVerify(employee.id)}
                      className="admin-employees__btn admin-employees__btn--verify admin-employees__btn--mobile"
                      disabled={verifyMutation.isPending}
                    >
                      Verify
                    </button>
                    <button
                      onClick={() => handleRejectClick(employee.id)}
                      className="admin-employees__btn admin-employees__btn--reject admin-employees__btn--mobile"
                      disabled={rejectMutation.isPending}
                    >
                      Reject
                    </button>
                  </>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="admin-employees__empty admin-employees__empty--mobile">No employees found</div>
        )}
      </div>

      {selectedUserId && (
        <UserDetailModal
          user={selectedUserDetails}
          documents={selectedUserDetails?.document_status?.uploaded_documents || []}
          userType="employee"
          isLoading={isLoadingDetails}
          onClose={handleCloseModal}
          onVerify={handleVerifyFromModal}
          onReject={handleRejectFromModal}
          onVerifyDocument={handleVerifyDocument}
          userId={selectedUserId}
        />
      )}

      {/* Confirm Verify Dialog */}
      <ConfirmDialog
        isOpen={!!showConfirmVerify}
        title="Verify Employee"
        message="Are you sure you want to verify this employee? This action cannot be undone."
        confirmText="Verify"
        cancelText="Cancel"
        type="success"
        onConfirm={handleConfirmVerify}
        onCancel={() => setShowConfirmVerify(null)}
      />

      {/* Reject Dialog */}
      <RejectDialog
        isOpen={!!showConfirmReject}
        title="Reject Employee"
        message="Please provide a reason for rejection:"
        confirmText="Reject"
        cancelText="Cancel"
        value={rejectComment}
        onChange={setRejectComment}
        onConfirm={handleConfirmReject}
        onCancel={() => {
          setShowConfirmReject(null);
          setRejectComment('');
        }}
      />
    </div>
  );
};

export default AdminEmployees;

